import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleCheckoutClick, { capture: true });
    this.addEventListener('submit', this.#handleCheckoutSubmit, { capture: true });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleCheckoutClick, { capture: true });
    this.removeEventListener('submit', this.#handleCheckoutSubmit, { capture: true });
    this.#historyAbortController?.abort();
  }

  /**
   * Closes the mobile drawer before navigating to checkout.
   * @param {HTMLButtonElement | HTMLAnchorElement | null} checkoutTrigger
   */
  #closeBeforeCheckout = async (checkoutTrigger = null) => {
    await closeDrawerBeforeCheckout(this, checkoutTrigger);
  };

  /**
   * Handles checkout button/link clicks inside the drawer.
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = getCheckoutTriggerFromElement(event.target);
    if (!(trigger instanceof HTMLButtonElement || trigger instanceof HTMLAnchorElement) || !this.contains(trigger)) return;

    if (!isCheckoutLikeTrigger(trigger)) return;
    if (consumeDelegatedCheckoutTrigger(trigger)) return;

    if (isShiprocketCheckoutTrigger(trigger)) {
      /* Shiprocket's injected buttons — including the cart BUY NOW with its
         inline buyCart onclick — must handle the click natively. Intercepting
         and replaying via internal direct-checkout APIs failed silently and
         left users on the product page with no checkout. We only defer the
         drawer close so the modal dialog doesn't block the checkout overlay. */
      scheduleDrawerClose(this);
      return;
    }

    event.preventDefault();
    this.#closeBeforeCheckout(trigger);
  };

  /**
   * Handles checkout form submits inside the drawer.
   * @param {SubmitEvent} event
   */
  #handleCheckoutSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !this.contains(event.target)) return;

    const submitter = event.submitter;
    if (submitter instanceof Element && consumeDelegatedCheckoutTrigger(submitter)) return;

    if (submitter instanceof Element && isShiprocketCheckoutTrigger(submitter)) {
      scheduleDrawerClose(this);
      return;
    }

    const isCheckoutForm =
      event.target.action.includes('/cart') ||
      event.target.action.includes('/checkout') ||
      (submitter instanceof HTMLElement && submitter.matches('[name="checkout"], .cart-checkout-btn'));

    if (!isCheckoutForm) return;

    event.preventDefault();
    event.stopPropagation();
    this.#closeBeforeCheckout(submitter instanceof HTMLButtonElement || submitter instanceof HTMLAnchorElement ? submitter : null);
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

const checkoutTriggerSelector =
  'button[name="checkout"], input[name="checkout"], .cart-checkout-btn, a[href="/checkout"], a[href*="/checkout"], [class*="checkout"], [id*="checkout"], [class*="buy"], [id*="buy"], [class*="releasit"], [id*="releasit"]';

const clickableSelector =
  'button, a, input[type="submit"], [role="button"], [onclick], .cart-checkout-btn, [class*="checkout"], [id*="checkout"], [class*="buy"], [id*="buy"], [class*="releasit"], [id*="releasit"]';

const checkoutTextPattern = /\b(buy|checkout|check out|proceed|place order|complete order|cash on delivery|cod)\b/i;
const nonCheckoutTextPattern = /\b(continue shopping|view cart|remove|apply|update|quantity|increase|decrease|plus|minus)\b/i;
const shiprocketTriggerSelector =
  '.shiprocket-headless, .sr-headless-checkout, .sr-checkout, .sr_checkout, .sr-checkout-btn, .sr_checkout_btn, .fastrr-checkout, .fastrr-checkout-btn, [class*="shiprocket"], [id*="shiprocket"], [class*="fastrr"], [id*="fastrr"]';
const delegatedCheckoutTriggers = new WeakSet();

/**
 * Finds an element in a composed event path.
 * @param {Event} event
 * @param {(element: Element) => boolean} predicate
 * @returns {Element | null}
 */
function findElementInEventPath(event, predicate) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (node instanceof Element && predicate(node)) return node;
  }

  return event.target instanceof Element && predicate(event.target) ? event.target : null;
}

/**
 * Finds the actionable element closest to a clicked target.
 * @param {Element} element
 * @returns {Element | null}
 */
function getCheckoutTriggerFromElement(element) {
  const trigger = element.closest(clickableSelector);
  return trigger && isCheckoutLikeTrigger(trigger) ? trigger : null;
}

/**
 * Finds a checkout-like trigger in a composed event path.
 * @param {Event} event
 * @returns {Element | null}
 */
function findCheckoutTriggerInEventPath(event) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (!(node instanceof Element)) continue;

    const trigger = getCheckoutTriggerFromElement(node);
    if (trigger) return trigger;
  }

  return event.target instanceof Element ? getCheckoutTriggerFromElement(event.target) : null;
}

/**
 * Checks whether an element is likely to initiate checkout or buy now.
 * @param {Element} trigger
 * @returns {boolean}
 */
function isCheckoutLikeTrigger(trigger) {
  const form = trigger instanceof HTMLButtonElement || trigger instanceof HTMLInputElement ? trigger.form : trigger.closest('form');
  const href = trigger instanceof HTMLAnchorElement ? trigger.getAttribute('href') || '' : '';
  const classAndId = `${trigger.id || ''} ${typeof trigger.className === 'string' ? trigger.className : ''}`;
  const text = `${trigger.textContent || ''} ${trigger.getAttribute('aria-label') || ''} ${
    trigger instanceof HTMLInputElement ? trigger.value || '' : ''
  }`.replace(/\s+/g, ' ').trim();

  if (nonCheckoutTextPattern.test(text)) return false;
  if (trigger.matches('[name="checkout"], .cart-checkout-btn')) return true;
  if (href.includes('/checkout')) return true;
  if (form?.action.includes('/checkout')) return true;
  if (form?.action.includes('/cart') && checkoutTextPattern.test(text)) return true;
  if (/(checkout|buy|releasit)/i.test(classAndId) && !nonCheckoutTextPattern.test(text)) return true;
  return checkoutTextPattern.test(text);
}

/**
 * Lets a checkout click we intentionally re-dispatch pass through to
 * Shiprocket/Fastrr and native browser handling.
 * @param {Element} trigger
 * @returns {boolean}
 */
function consumeDelegatedCheckoutTrigger(trigger) {
  if (!delegatedCheckoutTriggers.has(trigger)) return false;

  return true;
}

/**
 * Checks whether an element is one of Shiprocket/Fastrr's injected checkout controls.
 * @param {Element} trigger
 * @returns {boolean}
 */
function isShiprocketCheckoutTrigger(trigger) {
  /* closest(), not matches(): clicks usually land on the button's inner
     span/div (e.g. span.sr-checkout-visible2), whose classes do NOT match
     the Shiprocket selector even though the parent button does. With
     matches() those clicks fell into the generic checkout branch, which
     preventDefault-ed the event and killed Shiprocket's own handler. */
  return !!trigger.closest(shiprocketTriggerSelector);
}

/**
 * Finds a visible Shiprocket/Fastrr checkout control injected by the app.
 * @returns {HTMLElement | null}
 */
function findShiprocketCheckoutTrigger() {
  const candidates = document.querySelectorAll(shiprocketTriggerSelector);

  for (const candidate of candidates) {
    if (!(candidate instanceof HTMLElement)) continue;
    if (candidate instanceof HTMLInputElement && candidate.type === 'hidden') continue;
    if (candidate.closest('cart-drawer-component')) continue;
    if (!isCheckoutLikeTrigger(candidate) && !/(shiprocket|fastrr|checkout)/i.test(`${candidate.id} ${candidate.className}`)) continue;

    const rect = candidate.getBoundingClientRect();
    const style = window.getComputedStyle(candidate);
    if (rect.width <= 0 || rect.height <= 0 || style.display === 'none' || style.visibility === 'hidden') continue;

    return candidate;
  }

  return null;
}

/**
 * Replays checkout after the drawer is closed so Shiprocket/Fastrr listeners
 * can handle the same flow as Buy Now.
 * @param {Element | null} trigger
 */
function triggerCheckoutAfterDrawerClose(trigger) {
  const checkoutTrigger = findShiprocketCheckoutTrigger() || trigger;

  if (checkoutTrigger instanceof HTMLButtonElement || checkoutTrigger instanceof HTMLInputElement) {
    checkoutTrigger.disabled = false;
  }

  checkoutTrigger?.removeAttribute('aria-busy');

  if (checkoutTrigger instanceof HTMLElement) {
    delegatedCheckoutTriggers.add(checkoutTrigger);
    checkoutTrigger.click();
    window.setTimeout(() => delegatedCheckoutTriggers.delete(checkoutTrigger), 1000);
    return;
  }

  window.location.href = '/checkout';
}

/**
 * Finds the cart drawer affected by an event or the currently open drawer.
 * @param {Event} event
 * @returns {CartDrawerComponent | null}
 */
function findActiveCartDrawer(event) {
  const drawerFromPath = findElementInEventPath(event, (element) => element instanceof CartDrawerComponent);
  if (drawerFromPath instanceof CartDrawerComponent) return drawerFromPath;

  const openDialog = document.querySelector('cart-drawer-component dialog[open]');
  const openDrawer = openDialog?.closest('cart-drawer-component');
  return openDrawer instanceof CartDrawerComponent ? openDrawer : null;
}

/**
 * Force-cleans dialog state if the close animation event does not arrive.
 * @param {CartDrawerComponent} drawer
 */
function forceCloseDrawer(drawer) {
  const dialog = drawer.refs?.dialog;
  if (!dialog?.open) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  dialog.classList.remove('dialog-closing');
  dialog.close();
  drawer.dispatchEvent(new DialogCloseEvent());
}

/**
 * Closes the cart drawer without touching the checkout event so app checkout
 * listeners can continue handling the user's original tap.
 * @param {CartDrawerComponent} drawer
 */
async function closeDrawerOnly(drawer) {
  if (history.state?.cartDrawerOpen) {
    history.replaceState(null, '');
  }

  if (!drawer.refs?.dialog?.open) return;

  await Promise.race([
    drawer.closeDialog(),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);

  forceCloseDrawer(drawer);
}

/**
 * Defers drawer collapse until after target/bubble checkout handlers run.
 * @param {CartDrawerComponent} drawer
 */
function scheduleDrawerClose(drawer) {
  window.setTimeout(() => {
    closeDrawerOnly(drawer);
  }, 1200);
}

/**
 * Closes the cart drawer before checkout navigation. This capture-phase
 * fallback covers checkout buttons rendered or morphed inside nested cart
 * components where component-scoped listeners may miss the event.
 * @param {CartDrawerComponent} drawer
 * @param {Element | null} trigger
 */
async function closeDrawerBeforeCheckout(drawer, trigger) {
  if (trigger instanceof HTMLButtonElement) trigger.disabled = true;
  if (trigger instanceof HTMLInputElement) trigger.disabled = true;
  trigger?.setAttribute('aria-busy', 'true');

  if (history.state?.cartDrawerOpen) {
    history.replaceState(null, '');
  }

  await closeDrawerOnly(drawer);

  triggerCheckoutAfterDrawerClose(trigger);
}

document.addEventListener(
  'click',
  (event) => {
    if (event.defaultPrevented) return;

    const trigger = findCheckoutTriggerInEventPath(event);
    if (!trigger) return;
    if (consumeDelegatedCheckoutTrigger(trigger)) return;

    const drawer = findActiveCartDrawer(event);
    if (!(drawer instanceof CartDrawerComponent)) return;

    if (!isCheckoutLikeTrigger(trigger)) return;

    if (isShiprocketCheckoutTrigger(trigger)) {
      /* Let Shiprocket's own handlers (incl. cart buyCart) run natively;
         just collapse the drawer so it doesn't block the checkout overlay. */
      scheduleDrawerClose(drawer);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    closeDrawerBeforeCheckout(drawer, trigger);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) return;

    const drawer = event.target.closest('cart-drawer-component') || document.querySelector('cart-drawer-component dialog[open]')?.closest('cart-drawer-component');
    if (!(drawer instanceof CartDrawerComponent)) return;

    const submitter = event.submitter instanceof Element ? event.submitter : null;
    if (submitter && consumeDelegatedCheckoutTrigger(submitter)) return;

    if (submitter && isShiprocketCheckoutTrigger(submitter)) {
      scheduleDrawerClose(drawer);
      return;
    }

    const isCheckoutForm =
      event.target.action.includes('/cart') ||
      event.target.action.includes('/checkout') ||
      !!(submitter && isCheckoutLikeTrigger(submitter));

    if (!isCheckoutForm) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    closeDrawerBeforeCheckout(drawer, submitter);
  },
  true
);
